export type Vec2Tuple = [number, number];
export type Vec3Tuple = [number, number, number];

export interface SceneMaterialDefinition {
  color: string;
  roughness: number;
  metalness: number;
}

export interface GroundDefinition {
  size: Vec2Tuple;
  z: number;
  material: SceneMaterialDefinition;
}

export type OpenFace = 'north' | 'south' | 'east' | 'west';

export interface HangarDefinition {
  /** Base-center in world ENU metres. */
  position: Vec3Tuple;
  dimensions: Vec3Tuple;
  wallThickness: number;
  openFace: OpenFace;
  material: SceneMaterialDefinition;
}

export interface GateDefinition {
  id: string;
  /** Center of the opening in world ENU metres. */
  position: Vec3Tuple;
  /** Yaw about world +Z in radians. */
  orientation: number;
  innerSize: Vec2Tuple;
  postThickness: number;
  material: SceneMaterialDefinition;
}

export interface CrateDefinition {
  id: string;
  /** Base-center in world ENU metres. */
  position: Vec3Tuple;
  dimensions: Vec3Tuple;
  orientation: number;
  material: SceneMaterialDefinition;
}

export interface FieldSceneDefinition {
  id: 'field';
  ground: GroundDefinition;
  hangar: HangarDefinition;
  gates: GateDefinition[];
  crates: CrateDefinition[];
  lighting: {
    sunDirection: Vec3Tuple;
    sunColor: string;
    sunIntensity: number;
    ambientColor: string;
    ambientIntensity: number;
  };
  fog: { color: string; near: number; far: number };
  skybox: { topColor: string; bottomColor: string };
}

export type FieldScenePath =
  | 'scene'
  | 'id'
  | 'ground'
  | `ground.${string}`
  | 'hangar'
  | `hangar.${string}`
  | 'gates'
  | `gates[${number}]`
  | `gates[${number}].${string}`
  | 'crates'
  | `crates[${number}]`
  | `crates[${number}].${string}`
  | 'lighting'
  | `lighting.${string}`
  | 'fog'
  | `fog.${string}`
  | 'skybox'
  | `skybox.${string}`;

/** Validation failure with a machine-readable path into the scene document. */
export class FieldSceneValidationError extends Error {
  constructor(
    readonly path: FieldScenePath,
    message: string,
  ) {
    super(`${path}: ${message}`);
    this.name = 'FieldSceneValidationError';
  }
}

type UnknownRecord = Record<string, unknown>;

const fail = (path: FieldScenePath, message: string): never => {
  throw new FieldSceneValidationError(path, message);
};

const record = (value: unknown, path: FieldScenePath): UnknownRecord => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return fail(path, 'expected an object');
  }
  return value as UnknownRecord;
};

const finite = (value: unknown, path: FieldScenePath): number => {
  if (typeof value !== 'number' || !Number.isFinite(value))
    return fail(path, 'expected a finite number');
  return value;
};

const positive = (value: unknown, path: FieldScenePath): number => {
  const number = finite(value, path);
  if (number <= 0) return fail(path, 'expected a positive number');
  return number;
};

const text = (value: unknown, path: FieldScenePath): string => {
  if (typeof value !== 'string' || value.length === 0)
    return fail(path, 'expected a non-empty string');
  return value;
};

const color = (value: unknown, path: FieldScenePath): string => {
  const parsed = text(value, path);
  if (!/^#[0-9a-f]{6}$/i.test(parsed)) return fail(path, 'expected a six-digit hexadecimal colour');
  return parsed;
};

const vec2 = (value: unknown, path: FieldScenePath, positiveOnly = false): Vec2Tuple => {
  if (!Array.isArray(value) || value.length !== 2) return fail(path, 'expected a two-number array');
  const read = positiveOnly ? positive : finite;
  return [
    read(value[0], `${path}[0]` as FieldScenePath),
    read(value[1], `${path}[1]` as FieldScenePath),
  ];
};

const vec3 = (value: unknown, path: FieldScenePath, positiveOnly = false): Vec3Tuple => {
  if (!Array.isArray(value) || value.length !== 3)
    return fail(path, 'expected a three-number array');
  const read = positiveOnly ? positive : finite;
  return [
    read(value[0], `${path}[0]` as FieldScenePath),
    read(value[1], `${path}[1]` as FieldScenePath),
    read(value[2], `${path}[2]` as FieldScenePath),
  ];
};

const material = (value: unknown, path: FieldScenePath): SceneMaterialDefinition => {
  const input = record(value, path);
  const roughness = finite(input.roughness, `${path}.roughness` as FieldScenePath);
  const metalness = finite(input.metalness, `${path}.metalness` as FieldScenePath);
  if (roughness < 0 || roughness > 1) fail(`${path}.roughness` as FieldScenePath, 'expected 0..1');
  if (metalness < 0 || metalness > 1) fail(`${path}.metalness` as FieldScenePath, 'expected 0..1');
  return {
    color: color(input.color, `${path}.color` as FieldScenePath),
    roughness,
    metalness,
  };
};

export const parseFieldScene = (value: unknown): FieldSceneDefinition => {
  const input = record(value, 'scene');
  if (input.id !== 'field') fail('id', 'expected "field"');

  const groundInput = record(input.ground, 'ground');
  const hangarInput = record(input.hangar, 'hangar');
  const gatesInput: unknown[] = Array.isArray(input.gates)
    ? input.gates
    : fail('gates', 'expected an array');
  const cratesInput: unknown[] = Array.isArray(input.crates)
    ? input.crates
    : fail('crates', 'expected an array');
  const lightingInput = record(input.lighting, 'lighting');
  const fogInput = record(input.fog, 'fog');
  const skyboxInput = record(input.skybox, 'skybox');

  const wallThickness = positive(hangarInput.wallThickness, 'hangar.wallThickness');
  const hangarDimensions = vec3(hangarInput.dimensions, 'hangar.dimensions', true);
  if (
    wallThickness * 2 >=
    Math.min(hangarDimensions[0], hangarDimensions[1], hangarDimensions[2])
  ) {
    fail('hangar.wallThickness', 'must be less than half every hangar dimension');
  }
  const openFace = text(hangarInput.openFace, 'hangar.openFace');
  if (!['north', 'south', 'east', 'west'].includes(openFace)) {
    fail('hangar.openFace', 'expected north, south, east, or west');
  }

  const gates = gatesInput.map((item, index): GateDefinition => {
    const path = `gates[${index}]` as FieldScenePath;
    const gate = record(item, path);
    return {
      id: text(gate.id, `${path}.id` as FieldScenePath),
      position: vec3(gate.position, `${path}.position` as FieldScenePath),
      orientation: finite(gate.orientation, `${path}.orientation` as FieldScenePath),
      innerSize: vec2(gate.innerSize, `${path}.innerSize` as FieldScenePath, true),
      postThickness: positive(gate.postThickness, `${path}.postThickness` as FieldScenePath),
      material: material(gate.material, `${path}.material` as FieldScenePath),
    };
  });
  if (gates.length < 3 || gates.length > 4) fail('gates', 'expected three or four gates');
  if (new Set(gates.map((gate) => gate.id)).size !== gates.length)
    fail('gates', 'gate ids must be unique');

  const crates = cratesInput.map((item, index): CrateDefinition => {
    const path = `crates[${index}]` as FieldScenePath;
    const crate = record(item, path);
    return {
      id: text(crate.id, `${path}.id` as FieldScenePath),
      position: vec3(crate.position, `${path}.position` as FieldScenePath),
      dimensions: vec3(crate.dimensions, `${path}.dimensions` as FieldScenePath, true),
      orientation: finite(crate.orientation, `${path}.orientation` as FieldScenePath),
      material: material(crate.material, `${path}.material` as FieldScenePath),
    };
  });

  const sunDirection = vec3(lightingInput.sunDirection, 'lighting.sunDirection');
  if (sunDirection.every((component) => component === 0)) {
    fail('lighting.sunDirection', 'must not be the zero vector');
  }
  const fogNear = positive(fogInput.near, 'fog.near');
  const fogFar = positive(fogInput.far, 'fog.far');
  if (fogFar <= fogNear) fail('fog.far', 'must be greater than fog.near');

  return {
    id: 'field',
    ground: {
      size: vec2(groundInput.size, 'ground.size', true),
      z: finite(groundInput.z, 'ground.z'),
      material: material(groundInput.material, 'ground.material'),
    },
    hangar: {
      position: vec3(hangarInput.position, 'hangar.position'),
      dimensions: hangarDimensions,
      wallThickness,
      openFace: openFace as OpenFace,
      material: material(hangarInput.material, 'hangar.material'),
    },
    gates,
    crates,
    lighting: {
      sunDirection,
      sunColor: color(lightingInput.sunColor, 'lighting.sunColor'),
      sunIntensity: positive(lightingInput.sunIntensity, 'lighting.sunIntensity'),
      ambientColor: color(lightingInput.ambientColor, 'lighting.ambientColor'),
      ambientIntensity: positive(lightingInput.ambientIntensity, 'lighting.ambientIntensity'),
    },
    fog: { color: color(fogInput.color, 'fog.color'), near: fogNear, far: fogFar },
    skybox: {
      topColor: color(skyboxInput.topColor, 'skybox.topColor'),
      bottomColor: color(skyboxInput.bottomColor, 'skybox.bottomColor'),
    },
  };
};
